import { useState, useEffect, useRef } from "react";
import { useLocale } from "../../../context/LanguageContext";
import { usePreviewTemplate } from "../hooks/useNotifications";
import type { TriggerContract, NotificationPreview } from "../types";

interface TemplatePreviewProps {
  title: string;
  body: string;
  contract: TriggerContract | null;
}

export function TemplatePreview({ title, body, contract }: TemplatePreviewProps) {
  const { t } = useLocale();
  const [preview, setPreview] = useState<NotificationPreview | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [fetchPreview, { loading }] = usePreviewTemplate();

  // Build sample variables from contract examples
  const sampleVariables = contract
    ? contract.providedVariables.reduce<Record<string, string>>((acc, v) => {
        acc[v.name] = v.example;
        return acc;
      }, {})
    : {};

  useEffect(() => {
    if (!title.trim() && !body.trim()) {
      setPreview(null);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      fetchPreview({
        variables: {
          input: {
            title: title.trim(),
            body: body.trim(),
            sampleVariables: JSON.stringify(sampleVariables),
          },
        },
        onCompleted: (data) => {
          if (data?.previewNotificationTemplate) {
            setPreview(data.previewNotificationTemplate);
          }
        },
      });
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, body, JSON.stringify(sampleVariables)]);

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-foreground">{t("notifications.preview.livePreview")}</h4>

      {/* Mock push notification card */}
      <div className="max-w-sm rounded-xl border border-border bg-card p-4 shadow-lg">
        <div className="mb-2 flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary text-[10px] font-bold text-primary-foreground">
            K
          </div>
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Kommit
          </span>
          <span className="ml-auto text-[10px] text-muted-foreground">{t("notifications.preview.now")}</span>
        </div>

        {loading && !preview && (
          <p className="py-2 text-xs italic text-muted-foreground">{t("notifications.preview.rendering")}</p>
        )}

        {preview ? (
          <div>
            <p className="text-sm font-semibold text-foreground">{preview.title}</p>
            <p className="mt-0.5 text-sm text-muted-foreground">{preview.body}</p>

            {/* Validation feedback */}
            {!preview.validation.valid && preview.validation.errors.length > 0 && (
              <div className="mt-3 rounded border border-destructive/30 bg-destructive/5 p-2">
                <p className="mb-1 text-xs font-medium text-destructive">{t("notifications.preview.errors")}</p>
                {preview.validation.errors.map((err, i) => (
                  <p key={i} className="text-xs text-destructive">&bull; {err}</p>
                ))}
              </div>
            )}
            {preview.validation.warnings.length > 0 && (
              <div className="mt-2 rounded border border-yellow-500/30 bg-yellow-500/5 p-2">
                <p className="mb-1 text-xs font-medium text-yellow-700 dark:text-yellow-400">{t("notifications.preview.warnings")}</p>
                {preview.validation.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-yellow-700 dark:text-yellow-400">&bull; {w}</p>
                ))}
              </div>
            )}
          </div>
        ) : (
          !loading && (
            <p className="py-2 text-xs italic text-muted-foreground">
              {t("notifications.preview.startTyping")}
            </p>
          )
        )}
      </div>

      {/* Sample variables used */}
      {contract && contract.providedVariables.length > 0 && (
        <div className="text-xs text-muted-foreground">
          <p className="mb-1 font-medium">{t("notifications.preview.sampleVariables")}</p>
          <pre className="overflow-x-auto rounded bg-muted p-2 text-[11px]">
            {JSON.stringify(sampleVariables, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
